import { useState } from "react";
import { toast } from "sonner";
import { Facebook, Linkedin, MessageCircle, Copy, Twitter, Share2, Check } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Universal share bar for product / designer / collection pages.
 *
 * `type` decides which crawler-friendly backend share endpoint we point
 * social platforms at (so they fetch OG-tagged HTML — not the JS-only
 * React SPA). `id` is the listing / designer id.
 *
 * The URL sent to each platform gets a platform-specific UTM tag so the
 * traffic can be attributed in analytics, without polluting the canonical
 * URL that crawlers scrape (they see the `<link rel=canonical>` inside
 * the share page and de-dup accordingly).
 */
export default function ShareButtons({ type, id, title, description = "", compact = false }) {
  // Human-friendly canonical URL (React SPA route) — this is what we put on
  // clipboards and social platforms. Bot User-Agents hitting these URLs
  // are transparently rewritten to the backend's OG endpoint by
  // `frontend/src/setupProxy.js` (preview) or the equivalent edge rule
  // (production Cloudflare rule).
  const origin = typeof window !== "undefined" ? window.location.origin : "https://jeweltix.com";
  const path = type === "listing" ? `/design/${id}` : type === "designer" ? `/designer/${id}` : `/${type}/${id}`;
  const publicUrl = `${origin}${path}`;
  const desc = description || `${title} — on Jeweltix, the global marketplace for jewellery CAD files.`;
  const [copied, setCopied] = useState(false);

  // UTM parameters are appended to the human-facing URL. Crawlers strip
  // the query string via our JS/meta-refresh redirect, so canonical stays
  // clean and traffic attribution still works when the user actually taps.
  const withUtm = (source) => `${publicUrl}?utm_source=${source}&utm_medium=social&utm_campaign=share`;
  const open = (u) => window.open(u, "_blank", "noopener,noreferrer,width=740,height=640");

  const doWhatsApp  = () => open(`https://wa.me/?text=${encodeURIComponent(`${title}\n${withUtm("whatsapp")}`)}`);
  const doFacebook  = () => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withUtm("facebook"))}`);
  const doLinkedIn  = () => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withUtm("linkedin"))}`);
  const doTwitter   = () => open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(withUtm("twitter"))}&text=${encodeURIComponent(title)}`);

  const doInstagram = async () => {
    // Instagram has no direct share URL — copy the link + descriptive text
    // so the user can paste it into a Story or DM.
    try {
      await navigator.clipboard.writeText(`${title}\n${withUtm("instagram")}`);
      toast.success("Copied — paste into Instagram");
    } catch (_) {
      toast.error("Couldn't copy the link.");
    }
  };
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(withUtm("copy"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Share link copied");
    } catch (_) {
      toast.error("Couldn't copy the link.");
    }
  };
  const doNative = async () => {
    if (!navigator.share) return doCopy();
    try {
      await navigator.share({ title, text: desc, url: withUtm("native") });
    } catch (_) { /* user cancelled — no toast */ }
  };

  const buttons = [
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366", onClick: doWhatsApp },
    { key: "facebook", label: "Facebook", icon: Facebook,     color: "#1877F2", onClick: doFacebook },
    { key: "linkedin", label: "LinkedIn", icon: Linkedin,     color: "#0A66C2", onClick: doLinkedIn },
    { key: "twitter",  label: "X",        icon: Twitter,      color: "#000000", onClick: doTwitter },
    { key: "instagram", label: "Instagram", icon: Share2,     color: "#E4405F", onClick: doInstagram },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`share-buttons-${type}`}>
        {buttons.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={b.onClick}
            data-testid={`share-${b.key}`}
            title={`Share on ${b.label}`}
            className="h-8 w-8 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: b.color }}
          >
            <b.icon className="h-3.5 w-3.5" />
          </button>
        ))}
        <button
          type="button"
          onClick={doCopy}
          data-testid="share-copy"
          title="Copy link"
          className="h-8 w-8 rounded-full flex items-center justify-center border border-border hover:border-primary transition-colors"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button
            type="button"
            onClick={doNative}
            data-testid="share-native"
            title="More…"
            className="h-8 w-8 rounded-full flex items-center justify-center border border-border hover:border-primary transition-colors"
          >
            <Share2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid={`share-buttons-${type}`}>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Share this {type === "listing" ? "design" : type}</p>
      <div className="flex flex-wrap gap-2">
        {buttons.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={b.onClick}
            data-testid={`share-${b.key}`}
            className="flex items-center gap-1.5 text-sm rounded-md px-3 py-2 text-white font-medium hover:opacity-90 transition-opacity"
            style={{ backgroundColor: b.color }}
          >
            <b.icon className="h-4 w-4" /> {b.label}
          </button>
        ))}
        <button
          type="button"
          onClick={doCopy}
          data-testid="share-copy"
          className="flex items-center gap-1.5 text-sm rounded-md px-3 py-2 border border-border hover:border-primary transition-colors"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
